import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
  BusinessInfo,
  PurchaseOrder,
  PurchasingInvoiceDefaults,
  Supplier,
} from '../types';
import { computePoTotals, gstModeLabel } from './nzGst';

export interface PurchaseOrderPdfInput {
  po: PurchaseOrder;
  supplier: Supplier;
  businessInfo: BusinessInfo;
  defaults?: PurchasingInvoiceDefaults;
}

function formatDate(ms?: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function drawBusinessHeader(
  doc: jsPDF,
  businessInfo: BusinessInfo,
  title: string,
): number {
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = 26;
  doc.setFillColor(236, 72, 153);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text(businessInfo.businessName || 'Business', margin, 12);
  doc.setFontSize(9);
  let lineY = 17;
  if (businessInfo.address) {
    doc.text(businessInfo.address, margin, lineY);
    lineY += 4;
  }
  if (businessInfo.gstNumber) {
    doc.text(`GST #${businessInfo.gstNumber}`, margin, lineY);
    lineY += 4;
  }
  if (businessInfo.nzbn) {
    doc.text(`NZBN ${businessInfo.nzbn}`, margin, lineY);
  }
  doc.setTextColor(0, 0, 0);
  let y = headerHeight + 10;
  doc.setFontSize(18);
  doc.setFont(undefined as any, 'bold');
  doc.text(title, margin, y);
  doc.setFont(undefined as any, 'normal');
  return y + 8;
}

export function generatePurchaseOrderPdf(
  input: PurchaseOrderPdfInput,
  filename?: string,
): void {
  const { po, supplier, businessInfo, defaults } = input;
  const doc = new jsPDF({ orientation: 'portrait' });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const gstMode = po.gstMode ?? defaults?.defaultGstMode ?? 'inclusive';
  const totals = computePoTotals(po.items, gstMode);
  const paymentTerms = po.paymentTerms ?? supplier.paymentTerms ?? defaults?.defaultPaymentTerms;
  const deliveryAddress = po.deliveryAddress ?? businessInfo.address;

  let y = drawBusinessHeader(doc, businessInfo, 'PURCHASE ORDER');

  doc.setFontSize(10);
  doc.text(`PO #: ${po.id}`, margin, y);
  doc.text(`Date: ${formatDate(po.dateOrdered ?? po.dateCreated)}`, pageWidth / 2, y);
  y += 6;
  doc.text(`Status: ${po.status}`, margin, y);
  doc.text(`Pricing: ${gstModeLabel(gstMode)}`, pageWidth / 2, y);
  y += 10;

  doc.setFont(undefined as any, 'bold');
  doc.text('Supplier', margin, y);
  doc.setFont(undefined as any, 'normal');
  y += 5;
  doc.text(supplier.name, margin, y);
  y += 5;
  if (supplier.address) {
    doc.text(supplier.address, margin, y);
    y += 5;
  }
  if (supplier.contactPerson) {
    doc.text(`Contact: ${supplier.contactPerson}`, margin, y);
    y += 5;
  }
  if (supplier.phone) doc.text(`Phone: ${supplier.phone}`, margin, y);
  if (supplier.email) doc.text(`Email: ${supplier.email}`, pageWidth / 2, y);
  y += 10;

  doc.setFont(undefined as any, 'bold');
  doc.text('Deliver to', margin, y);
  doc.setFont(undefined as any, 'normal');
  y += 5;
  if (deliveryAddress) {
    doc.text(deliveryAddress, margin, y, { maxWidth: pageWidth - margin * 2 });
    y += 10;
  }

  const tableBody = po.items.map((line) => {
    const lineTotal = line.quantity * line.costPerUnit;
    return [
      line.productName,
      line.unit ?? 'units',
      String(line.quantity),
      `$${line.costPerUnit.toFixed(2)}`,
      `$${lineTotal.toFixed(2)}`,
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['Product', 'Unit', 'Qty', 'Unit price', 'Line total']],
    body: tableBody,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [236, 72, 153] },
    margin: { left: margin, right: margin },
  });

  y = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y + 40;
  y += 8;
  const totalsX = pageWidth - margin - 70;
  doc.setFontSize(10);
  doc.text('Subtotal (ex. GST):', totalsX, y);
  doc.text(`$${totals.subtotalExGst.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  y += 6;
  doc.text('GST (15%):', totalsX, y);
  doc.text(`$${totals.gstAmount.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  y += 6;
  doc.setFont(undefined as any, 'bold');
  doc.text('Total (inc. GST):', totalsX, y);
  doc.text(`$${totals.totalIncGst.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  doc.setFont(undefined as any, 'normal');
  y += 12;

  if (paymentTerms) {
    doc.text(`Payment terms: ${paymentTerms}`, margin, y);
    y += 6;
  }
  if (defaults?.bankAccountName || defaults?.bankAccountNumber) {
    const bank = [defaults.bankAccountName, defaults.bankAccountNumber].filter(Boolean).join(' — ');
    doc.text(`Bank: ${bank}`, margin, y);
    y += 6;
  }
  if (supplier.accountNumber) {
    doc.text(`Account ref: ${supplier.accountNumber}`, margin, y);
    y += 6;
  }
  if (po.notes) {
    doc.text(`Notes: ${po.notes}`, margin, y, { maxWidth: pageWidth - margin * 2 });
  }

  const safeId = po.id.replace(/[^\w-]/g, '_');
  doc.save(filename ?? `purchase-order-${safeId}.pdf`);
}

export function buildPurchaseOrderMailto(
  supplier: Supplier,
  po: PurchaseOrder,
): string {
  const subject = encodeURIComponent(`Purchase Order ${po.id}`);
  const body = encodeURIComponent(
    `Hi${supplier.contactPerson ? ` ${supplier.contactPerson}` : ''},\n\nPlease find our purchase order ${po.id} attached.\n\nThank you.`,
  );
  const email = supplier.email?.trim() || '';
  return email ? `mailto:${email}?subject=${subject}&body=${body}` : `mailto:?subject=${subject}&body=${body}`;
}
